import React, { useState } from 'react'
import { Icon as PhosphorIcon, CaretDown, CalendarBlank } from '@phosphor-icons/react'
import { Popover, PopoverContent, PopoverTrigger, Icon } from '@shopflo/ui'
import { Calendar } from 'src/components/common/calendar/calendar'
import { format } from 'date-fns'
import FormErrorText from 'src/components/common/FormErrorText'

export interface InlineDatePickerProps {
  date: Date | null
  onChange: (date?: Date) => void
  minDate?: Date
  error?: string
  onOpenChange?: (open: boolean) => void
  uiConfig: {
    label: string
    icon: PhosphorIcon
    placeholder?: string
    clearOptions?: {
      shouldShowClearButton?: boolean
      handleClear?: () => void
    }
  }
}

export const InlineDatePicker = ({ date, onChange, minDate, error, onOpenChange, uiConfig }: InlineDatePickerProps) => {
  const [open, setOpen] = useState(false)
  const { label, icon, placeholder, clearOptions } = uiConfig

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  return (
    <div className='flex flex-col w-full px-3 hover:bg-zinc-500/5 rounded-xl'>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            id={`date-picker-trigger-${label.toLowerCase().replace(/\s+/g, '-')}`}
            className='flex items-center justify-start w-full gap-1.5 py-3 border-none bg-transparent p-0 shadow-none hover:bg-transparent cursor-pointer'
          >
            <Icon IconComponent={icon} size='sm' className='text-zinc-400' />
            <span className='text-text-sm-regular text-zinc-500'>{label} </span>
            <span className='text-text-sm-semibold text-zinc-600 transition-colors group-hover:text-zinc-700'>
              {date ? format(date, 'dd MMM yyyy, hh:mm a') : placeholder}
            </span>
            <Icon IconComponent={CaretDown} size={14} className='text-zinc-400' />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align='start'
          className='w-auto p-0 bg-white !border-none !rounded-xl overflow-hidden'
          sideOffset={8}
        >
          <Calendar
            mode='single'
            selected={date ?? undefined}
            onSelect={date => {
              onChange(date)
            }}
            disabled={minDate ? { before: minDate } : undefined}
            showTime={true}
            clearOptions={
              clearOptions
                ? {
                    ...clearOptions,
                    handleClear: () => {
                      clearOptions.handleClear?.()
                      setOpen(false)
                    }
                  }
                : undefined
            }
          />
        </PopoverContent>
      </Popover>
      <FormErrorText message={error} />
    </div>
  )
}
